"use client";

import { useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import {
  navigateShellAware,
  navigateShellAwareBack,
} from "@/src/lib/bridge/shellAppNavigation";
import { getBridge } from "../lib/bridge";

/**
 * 对 `next/navigation` 的 `useRouter()` 二次封装，分流见 `navigateShellAware`：
 * - Tab 壳 → 非壳：`openDetailInNative`
 * - Detail 内 → 非壳：同 WebView `router.push` / `replace`
 * - Detail / Tab → 壳路径或跨 Tab 壳：`popDetailAndNavigateShell`
 * - `back`：Chat modal `dismissShellChat`；Detail `closeDetailInNative`；否则 `router.back`
 *
 * 用法：将 `const router = useRouter()` 替换为 `const router = useShellRouter()`。
 */
export function useShellRouter() {
  const router = useRouter();

  const push = useCallback(
    (href: string, options?: Parameters<typeof router.push>[1]) => {
      navigateShellAware(router, href, { routerOptions: options });
    },
    [router],
  );

  const replace = useCallback(
    (href: string, options?: Parameters<typeof router.replace>[1]) => {
      navigateShellAware(router, href, {
        replace: true,
        routerOptions: options,
      });
    },
    [router],
  );

  const back = useCallback(() => {
    navigateShellAwareBack(router);
  }, [router]);

  const shellRouter = useMemo(
    () => ({
      ...router,
      push,
      replace,
      back,
    }),
    [router, push, replace, back],
  );

  if (!getBridge().isInApp()) {
    return router;
  }

  return shellRouter;
}
